import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, MapPin, Calendar, User, CreditCard, Layers, Trophy, Trash2,
  Plus, Pencil, GitBranch, MonitorPlay, Check, Tv, Smartphone, Copy, Users, AlertTriangle, FileText, Gavel, Download, ChevronRight,
} from "lucide-react";
import AppHeader from "@/components/AppHeader";
import ImageUpload, { fileUrl } from "@/components/ImageUpload";
import Roster from "@/components/tournament/Roster";
import Bracket from "@/components/tournament/Bracket";
import Umpires from "@/components/tournament/Umpires";
import CustomFormBuilder from "@/components/tournament/CustomFormBuilder";
import CategoryDraftFields, { PLAYOFF_SCORING_PRESETS, scoringLabel } from "@/components/tournament/CategoryDraftFields";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CURRENCIES, FORMATS, PLAYOFF_FORMATS, STATUS_META, categoryLabel, fmtMoney, fmtDate } from "@/lib/tournament";
import { QRCodeCanvas } from "qrcode.react";
import api, { formatApiError } from "@/lib/api";

function QRBlock({ url, label, testid }) {
  const dl = () => {
    const c = document.getElementById(`qr-${testid}`);
    if (!c) return;
    const a = document.createElement("a");
    a.href = c.toDataURL("image/png");
    a.download = `${testid}-qr.png`;
    a.click();
  };
  return (
    <div className="rounded-2xl glow-border p-4 flex flex-col items-center gap-2" style={{ background: "var(--bg)" }} data-testid={`qr-block-${testid}`}>
      <div className="text-xs font-bold" style={{ color: "var(--text)" }}>{label}</div>
      <div className="bg-white p-2 rounded-xl"><QRCodeCanvas id={`qr-${testid}`} value={url} size={128} includeMargin /></div>
      <button data-testid={`qr-download-${testid}`} onClick={dl} className="glass rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1.5" style={{ color: "var(--text)" }}>
        <Download className="w-3.5 h-3.5 text-primary-c" /> Download
      </button>
    </div>
  );
}

const inputCls = "w-full rounded-xl bg-surface glow-border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-lime-400";
const STATUS_ORDER = ["draft", "published", "live", "completed"];

const TABS = [
  { id: "overview", label: "Overview", icon: Layers },
  { id: "registrations", label: "Registrations", icon: Users },
  { id: "bracketing", label: "Bracketing", icon: GitBranch },
  { id: "forms", label: "Reg. Form", icon: FileText },
  { id: "umpires", label: "Umpires & Scoring", icon: Gavel },
  { id: "live", label: "Live Dashboards", icon: MonitorPlay },
];

function Dropdown({ value, onChange, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="rounded-xl glow-border bg-surface" style={{ color: "var(--text)" }}><SelectValue /></SelectTrigger>
      <SelectContent className="glass" style={{ background: "var(--surface-glass)" }}>
        {options.map((o) => <SelectItem key={o.v} value={o.v} data-testid={`${testid}-${o.v}`}>{o.l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function TournamentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [t, setT] = useState(null);
  const [tab, setTab] = useState("overview");
  const [editOpen, setEditOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({ discipline: "doubles", skill_level: "open", gender: "mixed", format: "single_elim", pools: 1, scoring: PLAYOFF_SCORING_PRESETS[0].s });
  const [brkCat, setBrkCat] = useState("");
  // category edit state
  const [catEditOpen, setCatEditOpen] = useState(false);
  const [catDraft, setCatDraft] = useState(null);
  const [catOrig, setCatOrig] = useState(null);
  const [catBracketExists, setCatBracketExists] = useState(false);
  const [catWarnOpen, setCatWarnOpen] = useState(false);
  const [catSaving, setCatSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/tournaments/${id}`).then(({ data }) => setT(data)).catch(() => setT(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (t && t !== false && !brkCat && (t.categories || []).length > 0) setBrkCat(t.categories[0].id);
  }, [t, brkCat]);

  const patch = async (updates, silent) => {
    try {
      const { data } = await api.patch(`/tournaments/${id}`, updates);
      setT(data);
      if (!silent) toast.success("Saved");
      return data;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    }
  };

  const openEdit = () => {
    setForm({
      name: t.name, venue: t.venue || "", start_date: t.start_date || "", end_date: t.end_date || "",
      organizer_name: t.organizer_name || "", organizer_contact: t.organizer_contact || "",
      banner_url: t.banner_url, currency: t.currency || "PHP", entry_fee: t.entry_fee ?? 0,
      payment_qr_url: t.payment_qr_url, payment_details: t.payment_details || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    const res = await patch({ ...form, entry_fee: Number(form.entry_fee) || 0 }, true);
    setSaving(false);
    if (res) { setEditOpen(false); toast.success("Tournament updated"); }
  };

  const addCategory = () => {
    const dup = (t.categories || []).some((x) => x.discipline === draft.discipline && x.skill_level === draft.skill_level && x.gender === draft.gender && x.format === draft.format);
    if (dup) { toast.error("That category already exists"); return; }
    const cats = [...(t.categories || []).map((c) => ({ ...c, pools: c.pools || 1 })),
      { id: `cat_${Date.now()}`, ...draft, pools: draft.pools || 1 }];
    patch({ categories: cats });
  };

  const removeCategory = (cid) => {
    const cats = (t.categories || []).filter((c) => c.id !== cid).map((c) => ({ ...c, pools: c.pools || 1 }));
    patch({ categories: cats });
  };

  const openCatEdit = async (c) => {
    setCatDraft({ ...c, pools: c.pools || 1, playoff_format: c.playoff_format || "single_elim", scoring: c.scoring || PLAYOFF_SCORING_PRESETS[0].s });
    setCatOrig(c);
    setCatBracketExists(false);
    setCatEditOpen(true);
    try { await api.get(`/tournaments/${id}/categories/${c.id}/bracket`); setCatBracketExists(true); }
    catch { setCatBracketExists(false); }
  };

  const structuralChanged = () =>
    catOrig && catDraft && (
      catOrig.format !== catDraft.format ||
      (catOrig.pools || 1) !== (catDraft.pools || 1) ||
      catOrig.discipline !== catDraft.discipline
    );

  const doSaveCat = async () => {
    setCatSaving(true);
    const structural = catBracketExists && structuralChanged();
    if (structural) {
      // delete the existing bracket first so we never persist a category whose structure no longer matches its bracket
      try { await api.delete(`/tournaments/${id}/categories/${catDraft.id}/bracket`); }
      catch (e) {
        setCatSaving(false);
        toast.error(formatApiError(e.response?.data?.detail) || "Unlock this bracket before changing its structure");
        return;
      }
    }
    const cats = (t.categories || []).map((c) =>
      c.id === catDraft.id ? { ...catDraft, pools: catDraft.pools || 1 } : { ...c, pools: c.pools || 1 });
    const res = await patch({ categories: cats }, true);
    if (res) toast.success(structural ? "Category updated — bracket reset for rebuild" : "Category updated");
    setCatSaving(false);
    setCatWarnOpen(false);
    setCatEditOpen(false);
  };

  const saveCat = () => {
    if (catBracketExists && structuralChanged()) { setCatWarnOpen(true); return; }
    doSaveCat();
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/tournaments/${id}`);
      toast.success("Tournament deleted");
      nav("/tournaments");
    } catch (e) { toast.error("Delete failed"); }
  };

  if (t === null) return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}><Loader2 className="w-6 h-6 animate-spin text-primary-c" /></div>;
  if (t === false) return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <AppHeader title="Tournament" />
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <Trophy className="w-10 h-10 mx-auto text-primary-c opacity-60" />
        <h2 className="font-display font-bold text-xl mt-4">Tournament not found</h2>
        <button onClick={() => nav("/tournaments")} data-testid="notfound-back" className="btn-glow rounded-full px-5 py-2.5 mt-6 font-bold">Back to tournaments</button>
      </div>
    </div>
  );

  const sm = STATUS_META[t.status] || STATUS_META.draft;
  const cats = t.categories || [];

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <AppHeader title="Tournament Suite" />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8 grain">
        <button data-testid="detail-back" onClick={() => nav("/tournaments")} className="text-sm flex items-center gap-2 mb-5" style={{ color: "var(--text-2)" }}>
          <ArrowLeft className="w-4 h-4" /> All tournaments
        </button>

        {/* hero */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="rounded-3xl glow-border overflow-hidden" style={{ background: "var(--surface)" }} data-testid="detail-hero">
          <div className="relative aspect-[5/1] min-h-[120px]" style={{ background: "var(--bg)" }}>
            {t.banner_url ? <img src={fileUrl(t.banner_url)} alt="" className="w-full h-full object-cover" /> :
              <div className="w-full h-full flex items-center justify-center"><Trophy className="w-10 h-10 text-primary-c opacity-30" /></div>}
          </div>
          <div className="p-6 flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="font-display font-bold tracking-tight text-2xl sm:text-3xl truncate" data-testid="detail-name">{t.name}</h1>
                <span data-testid="detail-status" className="mono text-[10px] uppercase tracking-wider glass rounded-full px-2.5 py-1 flex items-center gap-1.5" style={{ color: sm.color }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: sm.color }} /> {sm.l}
                </span>
              </div>
              <div className="flex flex-wrap gap-4 mt-3 text-sm" style={{ color: "var(--text-2)" }}>
                {t.venue && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-primary-c" />{t.venue}</span>}
                {(t.start_date || t.end_date) && <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-primary-c" />{[fmtDate(t.start_date), fmtDate(t.end_date)].filter(Boolean).join(" → ")}</span>}
                {t.organizer_name && <span className="flex items-center gap-1.5"><User className="w-4 h-4 text-primary-c" />{t.organizer_name}</span>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="detail-players" onClick={() => nav(`/players?tid=${t.tournament_id}`)} className="glass rounded-full px-4 py-2 text-sm font-bold flex items-center gap-2" style={{ color: "var(--text)" }}><Users className="w-4 h-4 text-primary-c" /> Players</button>
              <button data-testid="detail-edit" onClick={openEdit} className="glass rounded-full px-4 py-2 text-sm font-bold flex items-center gap-2" style={{ color: "var(--text)" }}><Pencil className="w-4 h-4 text-primary-c" /> Edit</button>
              <button data-testid="detail-delete" onClick={() => setDelOpen(true)} className="glass rounded-full w-10 h-10 flex items-center justify-center"><Trash2 className="w-4 h-4" style={{ color: "#ef4444" }} /></button>
            </div>
          </div>
        </motion.div>

        {/* status control */}
        <div className="mt-5 rounded-2xl glow-border p-5 flex flex-wrap items-center gap-3" style={{ background: "var(--surface)" }} data-testid="status-control">
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: "var(--text-2)" }}>Stage</span>
          <div className="flex gap-1.5 flex-wrap">
            {STATUS_ORDER.map((s) => {
              const m = STATUS_META[s];
              const active = t.status === s;
              return (
                <button key={s} data-testid={`set-status-${s}`} onClick={() => !active && patch({ status: s })}
                  className="rounded-full px-3.5 py-1.5 text-xs font-bold flex items-center gap-1.5 transition-colors"
                  style={active ? { background: m.color, color: "#0a0f1d" } : { background: "var(--bg)", color: "var(--text-2)", border: "1px solid var(--border)" }}>
                  {active && <Check className="w-3 h-3" />} {m.l}
                </button>
              );
            })}
          </div>
        </div>

        {/* tabbed layout */}
        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-5 mt-6">
          {/* side nav */}
          <nav data-testid="detail-tabs" className="lg:sticky lg:top-6 self-start w-full relative">
            <div className="flex lg:flex-col gap-1.5 overflow-x-auto lg:overflow-visible rounded-2xl glow-border p-2" style={{ background: "var(--surface)" }}>
              {TABS.map((tb) => {
                const Icon = tb.icon;
                const active = tab === tb.id;
                return (
                  <button key={tb.id} data-testid={`tab-${tb.id}`} onClick={() => setTab(tb.id)}
                    className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-bold whitespace-nowrap transition-colors shrink-0 lg:w-full"
                    style={active
                      ? { background: "var(--primary)", color: "var(--on-primary)" }
                      : { color: "var(--text-2)" }}>
                    <Icon className="w-4 h-4" /> {tb.label}
                  </button>
                );
              })}
            </div>
            <div className="lg:hidden pointer-events-none absolute right-0 top-0 bottom-0 w-10 rounded-r-2xl flex items-center justify-end" style={{ background: "linear-gradient(to right, transparent, var(--surface))" }}>
              <ChevronRight className="w-3.5 h-3.5 mr-1 opacity-60" style={{ color: "var(--text-2)" }} />
            </div>
          </nav>

          {/* content */}
          <div className="min-w-0">
            {tab === "overview" && (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5" data-testid="panel-overview">
                {/* categories */}
                <div className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }} data-testid="detail-categories">
                  <h3 className="font-display font-bold flex items-center gap-2 mb-4"><Layers className="w-4 h-4 text-primary-c" /> Categories <span className="text-xs mono" style={{ color: "var(--text-2)" }}>({cats.length})</span></h3>
                  <div className="rounded-xl p-3 mb-3" style={{ background: "var(--bg)" }}>
                    <CategoryDraftFields draft={draft} setDraft={setDraft} idPrefix="detail-draft" />
                  </div>
                  <button data-testid="detail-add-category" onClick={addCategory} className="btn-glow rounded-full px-4 py-2 text-sm font-bold inline-flex items-center gap-2 mb-3"><Plus className="w-4 h-4" /> Add category</button>
                  <div className="space-y-2">
                    {cats.length === 0 ? <p className="text-sm text-center py-4" style={{ color: "var(--text-2)" }}>No categories yet.</p> :
                      cats.map((c) => (
                        <div key={c.id} data-testid={`detail-category-${c.id}`} className="flex items-center justify-between rounded-xl px-4 py-2.5 glass">
                          <div><div className="font-bold text-sm">{categoryLabel(c)}</div><div className="text-[10px] mono uppercase mt-0.5" style={{ color: "var(--text-2)" }}>{FORMATS.find((f) => f.v === c.format)?.l}{(c.pools || 1) > 1 ? ` · ${c.pools} pools · playoff: ${PLAYOFF_FORMATS.find((f) => f.v === (c.playoff_format || "single_elim"))?.l}` : ""}{scoringLabel(c.scoring) ? ` · ${scoringLabel(c.scoring)}` : ""}</div></div>
                          <div className="flex items-center gap-1.5">
                            <button data-testid={`detail-edit-category-${c.id}`} onClick={() => openCatEdit(c)} className="w-8 h-8 rounded-full glass flex items-center justify-center"><Pencil className="w-3.5 h-3.5 text-primary-c" /></button>
                            <button data-testid={`detail-remove-category-${c.id}`} onClick={() => removeCategory(c.id)} className="w-8 h-8 rounded-full glass flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" style={{ color: "#ef4444" }} /></button>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                {/* payment */}
                <div className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }} data-testid="detail-payment">
                  <h3 className="font-display font-bold flex items-center gap-2 mb-4"><CreditCard className="w-4 h-4 text-primary-c" /> Payment & fees</h3>
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="font-display font-black text-3xl tracking-tighter">{fmtMoney(t.currency, t.entry_fee)}</span>
                    <span className="text-xs" style={{ color: "var(--text-2)" }}>per player entry fee</span>
                  </div>
                  {t.payment_qr_url && <img src={fileUrl(t.payment_qr_url)} alt="payment qr" className="w-40 h-40 object-contain rounded-xl glow-border p-2 mb-3" style={{ background: "#fff" }} data-testid="detail-qr" />}
                  {t.payment_details ? <p className="text-sm whitespace-pre-wrap rounded-xl p-3" style={{ background: "var(--bg)", color: "var(--text-2)" }} data-testid="detail-pay-details">{t.payment_details}</p> :
                    <p className="text-sm" style={{ color: "var(--text-2)" }}>No payment instructions set.</p>}
                </div>
              </div>
            )}

            {tab === "registrations" && (
              <div data-testid="panel-registrations"><Roster tournament={t} /></div>
            )}

            {tab === "bracketing" && (
              <div className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }} data-testid="brackets-panel">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
                  <h3 className="font-display font-bold text-lg flex items-center gap-2"><GitBranch className="w-5 h-5 text-primary-c" /> Brackets & Seeding</h3>
                  {cats.length > 0 && (
                    <Select value={brkCat} onValueChange={setBrkCat}>
                      <SelectTrigger data-testid="bracket-category-select" className="rounded-full glow-border bg-surface w-auto min-w-[220px]" style={{ color: "var(--text)" }}><SelectValue placeholder="Select category" /></SelectTrigger>
                      <SelectContent className="glass" style={{ background: "var(--surface-glass)" }}>
                        {cats.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`bracket-cat-opt-${c.id}`}>{categoryLabel(c)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                {cats.length === 0 ? (
                  <p className="text-sm" style={{ color: "var(--text-2)" }}>Add a category first to build a bracket.</p>
                ) : brkCat ? (
                  <Bracket key={brkCat} tournament={t} category={cats.find((c) => c.id === brkCat)} />
                ) : (
                  <p className="text-sm" style={{ color: "var(--text-2)" }}>Select a category above to generate or view its bracket.</p>
                )}
              </div>
            )}

            {tab === "forms" && (
              <div data-testid="panel-forms">
                <CustomFormBuilder initial={t.custom_fields || []} onSave={(fields) => patch({ custom_fields: fields }, true)} />
              </div>
            )}

            {tab === "umpires" && (
              <div data-testid="panel-umpires"><Umpires tournament={t} onUpdate={patch} /></div>
            )}

            {tab === "live" && (
              <div className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }} data-testid="live-views-panel">
                <h3 className="font-display font-bold flex items-center gap-2 mb-1"><MonitorPlay className="w-4 h-4 text-primary-c" /> Live Dashboards</h3>
                <p className="text-xs mb-4" style={{ color: "var(--text-2)" }}>
                  {t.status === "draft" ? "Publish the tournament to open the live views." : "Big-screen scoreboard for the venue and a QR spectators scan to follow along."}
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <a href={`${window.location.origin}/scoreboard/${t.tournament_id}`} target="_blank" rel="noreferrer" data-testid="open-scoreboard"
                    className="btn-glow rounded-full px-4 py-2.5 text-sm font-bold flex items-center gap-2"><Tv className="w-4 h-4" /> TV Scoreboard</a>
                  <a href={`${window.location.origin}/spectator/${t.tournament_id}`} target="_blank" rel="noreferrer" data-testid="open-spectator"
                    className="glass rounded-full px-4 py-2.5 text-sm font-bold flex items-center gap-2" style={{ color: "var(--text)" }}><Smartphone className="w-4 h-4 text-primary-c" /> Spectator page</a>
                  <button data-testid="copy-spectator" onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/spectator/${t.tournament_id}`); toast.success("Spectator link copied"); }}
                    className="glass rounded-full px-4 py-2.5 text-sm font-bold flex items-center gap-2" style={{ color: "var(--text)" }}><Copy className="w-4 h-4 text-primary-c" /> Copy link</button>
                </div>
                <div className="grid grid-cols-2 sm:flex gap-4 mt-5" data-testid="qr-codes">
                  <QRBlock testid="registration" label="Registration portal" url={`${window.location.origin}/t/${t.tournament_id}`} />
                  <QRBlock testid="spectator" label="Spectator page" url={`${window.location.origin}/spectator/${t.tournament_id}`} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="glass max-h-[85vh] overflow-y-auto" style={{ background: "var(--surface-glass)" }} data-testid="edit-dialog">
          <DialogHeader><DialogTitle className="font-display">Edit tournament</DialogTitle><DialogDescription style={{ color: "var(--text-2)" }}>Update event details and payment settings.</DialogDescription></DialogHeader>
          {form && (
            <div className="space-y-3">
              <input data-testid="edit-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Name" className={inputCls} style={{ color: "var(--text)" }} />
              <input data-testid="edit-venue" value={form.venue} onChange={(e) => setForm((f) => ({ ...f, venue: e.target.value }))} placeholder="Venue" className={inputCls} style={{ color: "var(--text)" }} />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="edit-start" type="date" value={form.start_date || ""} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} className={inputCls} style={{ color: "var(--text)" }} />
                <input data-testid="edit-end" type="date" value={form.end_date || ""} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} className={inputCls} style={{ color: "var(--text)" }} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="edit-org-name" value={form.organizer_name} onChange={(e) => setForm((f) => ({ ...f, organizer_name: e.target.value }))} placeholder="Organizer name" className={inputCls} style={{ color: "var(--text)" }} />
                <input data-testid="edit-org-contact" value={form.organizer_contact} onChange={(e) => setForm((f) => ({ ...f, organizer_contact: e.target.value }))} placeholder="Organizer contact" className={inputCls} style={{ color: "var(--text)" }} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Dropdown testid="edit-currency" value={form.currency} onChange={(v) => setForm((f) => ({ ...f, currency: v }))} options={CURRENCIES} />
                <input data-testid="edit-fee" type="number" min="0" value={form.entry_fee} onChange={(e) => setForm((f) => ({ ...f, entry_fee: e.target.value }))} placeholder="Entry fee" className={inputCls} style={{ color: "var(--text)" }} />
              </div>
              <ImageUpload testid="edit-banner" label="Banner" value={form.banner_url} onChange={(v) => setForm((f) => ({ ...f, banner_url: v }))} />
              <ImageUpload testid="edit-qr" label="Payment QR" value={form.payment_qr_url} onChange={(v) => setForm((f) => ({ ...f, payment_qr_url: v }))} aspect="aspect-square" />
              <textarea data-testid="edit-pay-details" rows={3} value={form.payment_details} onChange={(e) => setForm((f) => ({ ...f, payment_details: e.target.value }))} placeholder="Payment instructions" className={`${inputCls} resize-none`} style={{ color: "var(--text)" }} />
            </div>
          )}
          <DialogFooter>
            <button data-testid="edit-cancel" onClick={() => setEditOpen(false)} className="glass rounded-full px-5 py-2.5 text-sm font-bold" style={{ color: "var(--text)" }}>Cancel</button>
            <button data-testid="edit-save" onClick={saveEdit} disabled={saving} className="btn-glow rounded-full px-5 py-2.5 text-sm font-bold flex items-center gap-2 disabled:opacity-60">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* category edit dialog */}
      <Dialog open={catEditOpen} onOpenChange={setCatEditOpen}>
        <DialogContent className="glass max-h-[85vh] overflow-y-auto" style={{ background: "var(--surface-glass)" }} data-testid="cat-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Edit category</DialogTitle>
            <DialogDescription style={{ color: "var(--text-2)" }}>Adjust skill tier, division, format, brackets and playoff scoring — even after this category is live.</DialogDescription>
          </DialogHeader>
          {catDraft && (
            <div className="space-y-3">
              <CategoryDraftFields draft={catDraft} setDraft={setCatDraft} idPrefix="cat-edit" />
              {catBracketExists && structuralChanged() && (
                <div className="rounded-xl p-3 flex items-start gap-2 text-xs" style={{ background: "rgba(234,179,8,0.12)", color: "#eab308" }} data-testid="cat-edit-warning">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>This category already has a bracket. Changing the format, number of brackets or discipline will <b>reset the bracket and clear recorded results</b> so it can be rebuilt.</span>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <button data-testid="cat-edit-cancel" onClick={() => setCatEditOpen(false)} className="glass rounded-full px-5 py-2.5 text-sm font-bold" style={{ color: "var(--text)" }}>Cancel</button>
            <button data-testid="cat-edit-save" onClick={saveCat} disabled={catSaving} className="btn-glow rounded-full px-5 py-2.5 text-sm font-bold flex items-center gap-2 disabled:opacity-60">{catSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save category"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* structural change warning */}
      <AlertDialog open={catWarnOpen} onOpenChange={setCatWarnOpen}>
        <AlertDialogContent className="glass" style={{ background: "var(--surface-glass)" }} data-testid="cat-reset-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display flex items-center gap-2"><AlertTriangle className="w-5 h-5" style={{ color: "#eab308" }} /> Rebuild bracket?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: "var(--text-2)" }}>
              {catDraft && <>Changing the structure of <span className="font-bold">{categoryLabel(catDraft)}</span> will delete its existing bracket and all recorded results so it can be regenerated. This cannot be undone.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cat-reset-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="cat-reset-confirm" onClick={(e) => { e.preventDefault(); doSaveCat(); }} className="bg-amber-500 hover:bg-amber-600 text-black">Save & rebuild</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* delete */}
      <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
        <AlertDialogContent className="glass" style={{ background: "var(--surface-glass)" }} data-testid="tournament-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display flex items-center gap-2"><Trash2 className="w-5 h-5" style={{ color: "#ef4444" }} /> Delete tournament?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: "var(--text-2)" }}>This permanently removes <span className="font-bold">{t.name}</span> and all its registrations. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="tournament-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="tournament-delete-confirm" onClick={(e) => { e.preventDefault(); confirmDelete(); }} className="bg-red-600 hover:bg-red-700 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
